import { releaseAssetPath } from '@/game/assets/release-assets';
import { Rectangle, Texture } from 'pixi.js';
import { acquireSpriteSource, releaseSpriteSource } from '@/game/assets/gameSpriteRegistry';

export interface AnimationAtlas {
  url: string; width: number; height: number; columns: number; rows?: number; scaleMode: 'nearest' | 'linear';
}
interface Entry {
  error?: Error;
  definition: AnimationAtlas; bytes: number; owners: Set<AnimationAtlasCache>; sourceOwner: object;
  source?: Texture['source'];
  frames: readonly Texture[] | null; pending: boolean; retryAt: number; release?: Promise<void>; releasing?: boolean; promise: Promise<void>;
}
interface AtlasPool { entries: Map<string, Entry>; reservedBytes: number; maxBytes: number }
// Admin and game leases share sources, but must never share admission accounting.
const pools = new Map<number, AtlasPool>();
// A full idle atlas decodes to about 9 MiB. A city needs one atlas per role
// at once, alongside the ambient inhabitants and any nearby hero variants.
const MAX_BYTES = 192 * 1024 * 1024;
function releaseEntry(pool: AtlasPool, key: string, entry: Entry): void {
  if (entry.owners.size || entry.releasing) return;
  // Cancellation removes semaphore waiters immediately. Keep the reservation until
  // decode and final unload finish, so a replacement cannot exceed the byte cap.
  entry.release ??= releaseSpriteSource(entry.definition.url, entry.sourceOwner);
  if (entry.pending) return;
  entry.releasing = true;
  for (const frame of entry.frames ?? []) frame.destroy(false);
  entry.frames = null;
  void entry.release.then(() => {
    if (pool.entries.get(key) === entry) pool.entries.delete(key);
    pool.reservedBytes -= entry.bytes;
  });
}
/** Leases survive until sprites have been rebound/removed at endFrame, including async loads. */
export class AnimationAtlasCache {
  private readonly pool: AtlasPool;
  constructor({ maxBytes = MAX_BYTES }: { maxBytes?: number } = {}) {
    let pool = pools.get(maxBytes);
    if (!pool) {
      pool = { entries: new Map(), reservedBytes: 0, maxBytes };
      pools.set(maxBytes, pool);
    }
    this.pool = pool;
  }
  private leases = new Map<string, Entry>();
  private sourceLeases = new Map<Texture['source'], Set<string>>();
  private demanded = new Set<string>();
  private admissionRefused = new Set<string>();
  private disposed = false;
  get frameError(): Error | undefined { return [...this.demanded].map(key => this.leases.get(key)?.error).find(error => !!error); }
  get frameAdmissionError(): Error | undefined {
    return [...this.demanded].some(key => this.admissionRefused.has(key))
      ? new Error('Visible animation cannot fit the startup GPU budget; reload required') : undefined;
  }
  get frameReady(): boolean { return [...this.demanded].every(key => !!this.leases.get(key)?.frames); }
  beginFrame(): void { this.demanded.clear(); this.admissionRefused.clear(); }
  retainTexture(texture: Texture): void {
    const keys = this.sourceLeases.get(texture.source);
    if (keys) for (const key of keys) this.demanded.add(key);
  }
  endFrame(): void {
    for (const [key, entry] of this.leases) {
      if (!this.demanded.has(key)) {
        this.unindexLease(key, entry);
        this.leases.delete(key); entry.owners.delete(this); releaseEntry(this.pool, key, entry);
      }
    }
  }
  private markAdmissionRefused(key: string, bytes: number): void {
    // Replacement sessions can overlap asynchronous unloads. Their reservations
    // still enforce the cap, but are not evidence that this scene cannot fit.
    const releasing = [...this.pool.entries.values()].reduce((sum, entry) => sum + (entry.release ? entry.bytes : 0), 0);
    if (!Number.isSafeInteger(bytes) || bytes <= 0 || this.pool.reservedBytes - releasing + bytes > this.pool.maxBytes) {
      this.admissionRefused.add(key);
    }
  }
  protected requestAtlas(definition: AnimationAtlas): readonly Texture[] | null {
    if (this.disposed) return null;
    const { url, width, height, columns, rows = 1, scaleMode } = definition;
    const pool = this.pool;
    const key = `${url}:${width}:${height}:${columns}:${rows}:${scaleMode}`;
    this.demanded.add(key);
    let entry = pool.entries.get(key);
    if (!entry) {
      const bytes = width * columns * height * rows * 4;
      if (!Number.isSafeInteger(bytes) || bytes <= 0 || pool.reservedBytes + bytes > pool.maxBytes) {
        this.markAdmissionRefused(key, bytes); return null;
      }
      entry = { definition, bytes: 0, owners: new Set(), sourceOwner: {}, frames: null, pending: false, retryAt: 0, promise: Promise.resolve() };
      pool.entries.set(key, entry);
    }
    if (entry.release) return null;
    if (!entry.pending && !entry.frames && Date.now() >= entry.retryAt) {
      const bytes = width * columns * height * rows * 4;
      if (pool.reservedBytes + bytes > pool.maxBytes) { this.markAdmissionRefused(key, bytes); return null; }
      entry.bytes = bytes; entry.pending = true; pool.reservedBytes += bytes;
      const current = entry;
      current.promise = acquireSpriteSource(url, current.sourceOwner).then(atlas => {
        if (current.owners.size && atlas.width === width * columns && atlas.height === height * rows) {
          atlas.source.scaleMode = scaleMode;
          current.frames = Array.from({ length: columns * rows }, (_, index) => new Texture({ source: atlas.source,
            frame: new Rectangle((index % columns) * width, Math.floor(index / columns) * height, width, height) }));
          current.source = atlas.source;
          for (const owner of current.owners) owner.indexLease(key, current);
        } else if (current.owners.size && releaseAssetPath(url)) {
          throw new Error('Pinned animation layout incompatible; reload required');
        }
      }).catch(error => {
        if (releaseAssetPath(url)) current.error = new Error(`Pinned animation unavailable; reload required: ${url}: ${String(error)}`);
      }).finally(() => {
        current.pending = false;
        if (!current.frames && current.owners.size) {
          releaseSpriteSource(url, current.sourceOwner);
          pool.reservedBytes -= current.bytes; current.bytes = 0; current.retryAt = Date.now() + 5000;
        }
        releaseEntry(pool, key, current);
      });
    }
    entry.owners.add(this); this.leases.set(key, entry);
    if (entry.frames) this.indexLease(key, entry);
    return entry.frames;
  }
  private indexLease(key: string, entry: Entry): void {
    if (!entry.source) return;
    let keys = this.sourceLeases.get(entry.source);
    if (!keys) this.sourceLeases.set(entry.source, keys = new Set());
    keys.add(key);
  }
  private unindexLease(key: string, entry: Entry): void {
    if (!entry.source) return;
    const keys = this.sourceLeases.get(entry.source);
    keys?.delete(key);
    if (!keys?.size) this.sourceLeases.delete(entry.source);
  }
  async waitForLoads(): Promise<void> { await Promise.all([...this.leases.values()].map(entry => entry.promise)); }
  stats(): { maxBytes: number; reservedBytes: number; loaded: number; pending: number } {
    return { maxBytes: this.pool.maxBytes, reservedBytes: this.pool.reservedBytes, loaded: [...this.pool.entries.values()].filter(entry => !!entry.frames).length,
      pending: [...this.pool.entries.values()].filter(entry => entry.pending).length };
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.demanded.clear(); this.admissionRefused.clear();
    for (const [key, entry] of this.leases) { entry.owners.delete(this); releaseEntry(this.pool, key, entry); }
    this.leases.clear();
    this.sourceLeases.clear();
  }
}
